<template>
  <div class="ak-empty-state" :class="{ 'ak-empty-state--inverse': inverse }">
    <div class="ak-empty-state__body">
      <div v-if="icon" class="ak-empty-state__icon">
        <ak-icon :type="icon" :size="iconSize" :fa="fa" :fa-light="faLight" />
      </div>
      <h3 v-if="title" class="ak-empty-state__title">{{ title }}</h3>
      <slot />
      <div class="mt-small">
        <ak-button v-if="button" size="small" @click="click()">
          {{ button }}
        </ak-button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Icon from '../icon/Icon.vue'
import Button from '../button/Button.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-empty-state title="Nothing to see here...">
   * </ak-empty-state>
   */
  name: 'AkEmptyState',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [Button.name]: Button,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description The title to display in the empty state.
     */
    title: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description The icon type to display in the empty state.
     */
    icon: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description The size of the icon for the empty state.
     */
    iconSize: {
      type: String,
      default: 'huge',
    },

    /**
     * @type {Boolean}
     * @description Inverse the colors
     */
    inverse: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Optional button text
     */
    button: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Function}
     * @description Optional button function
     */
    click: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Boolean}
     * @description Controls if the icon is using a font awesome icon or not
     */
    fa: {
      default: false,
      type: Boolean,
    },

    /**
     * @type {Boolean}
     * @description Controls if the font awesome icon is light
     */
    faLight: {
      default: false,
      type: Boolean,
    },
  },
})
</script>
<style lang="scss">
@import '../../scss/_settings.scss';
.ak-empty-state {
  $self: &;
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  padding: 0 $spacing $spacing;

  &--new {
    border: 2px dashed $color-grey-light;
    border-radius: $base-border-radius;
    padding: $spacing;
  }

  &__body {
    max-width: 600px;
    width: 100%;
  }

  &__icon {
    margin-bottom: $spacing-small;
    color: $color-grey-light;

    #{$self}--inverse & {
      color: #fff;
    }
  }

  &__title {
    color: $color-grey;
    margin-bottom: $spacing-tiny;

    #{$self}--inverse & {
      color: #fff;
    }
  }
}

.ak-table .ak-empty-state {
  display: flex;
}
</style>
